<template>
	<div class="upload-document-container">
		<!-- Dialog upload document -->
		<el-dialog 
			v-if="state.isVisible" 
			v-model="state.isVisible" 
			width="454px" 
			:show-close="false" 
			:close-on-click-modal="false"
		>
			<template #header> <span> Upload document </span> </template>
			<div class="upload-document-container__body">
				<el-form
					label-position="left"
					label-width="100px"
					enctype="multipart/form-data"
					novalidate
					ref="uploadForm"
				>
					<!-- Customer Name Row -->
					<div class="upload-document-container__body__custom-row">
						<div class="upload-document-container__body__document-key">
							{{ t("CUSTOMER_NAME") }}
						</div>
						<div class="upload-document-container__body__document-value">
							<div v-if="state.isInitialLoading" class="skeleton-loading">
								<div class="skeleton-text skeleton-text--medium"></div>
							</div>
							<span v-else>{{ state.form?.CustomerName || t("NO_DATA") }}</span>
						</div>
					</div>

					<!-- Property Reference Row -->
					<div class="upload-document-container__body__custom-row">
						<div class="upload-document-container__body__document-key">
							{{ t("OBJECT") }}
						</div>
						<div class="upload-document-container__body__document-value">
							<div v-if="state.isInitialLoading" class="skeleton-loading">
								<div class="skeleton-text skeleton-text--long"></div>
							</div>
							<span v-else>{{ state.form?.PropertyReference || t("NO_DATA") }}</span>
						</div>
					</div>

					<!-- Documents Row -->
					<div class="upload-document-container__body__custom-row">
						<div class="upload-document-container__body__document-key">
							{{ t("TABTITLE_DOCUMENTS") }}
						</div>
						<div class="upload-document-container__body__document-value">
							<div v-if="state.isInitialLoading" class="skeleton-loading">
								<div class="skeleton-text skeleton-text--short"></div>
							</div>
							<span v-else>{{ documentTypeNames || t("NO_DATA") }}</span>
						</div>
					</div>
					<!-- Upload Section -->
					<el-form-item>
						<div v-if="state.isInitialLoading" class="skeleton-loading">
							<div class="skeleton-upload-box"></div>
						</div>
						<div v-else>
							<el-upload
								class="upload-document-container__body__dropbox"
								drag
								:action="`${Config.serverUrl}/external-supplier/upload-tmp-document?token=${state.token}`"
								:headers="{
									Authorization:
										'Bearer _Vl31UCvzJYe5t2g7WMH4BIA65IA3rY0oHe0Cpd1RVA.j3gC_H-kjOvFtgXp26MVRoaxVb4xhkilTCB_U-pcKeQ' //Random token
								}"
								:on-remove="handleRemove"
								:before-upload="handleBeforeUpload"
								:on-error="handleError"
								multiple
								:file-list="state.fileList"
								:on-success="handleSuccess"
							>
								<div>
									{{ t("DROP_FILE_TO_UPLOAD_OR") }}
								</div>
								<el-button class="choose-file-btn">
									{{ t("CHOOSE_FILE") }}
								</el-button>
							</el-upload>
						</div>
					</el-form-item>

					<!-- Submit Button -->
					<div v-if="state.isInitialLoading" class="skeleton-loading">
						<div class="skeleton-button"></div>
					</div>
					<el-button
						v-else
						type="primary"
						@click="submitForm"
						:class="{
							'submit-btn': true,
							'submit-btn--disabled': state.uploadingCount
						}"
						size="medium"
						:loading="state.loading"
						:disabled="!!state.uploadingCount || !state.fileList?.length"
					>
						{{ t("SUBMIT") }}
					</el-button>
				</el-form>
			</div>
		</el-dialog>

		<!-- Success state -->
		<el-container
			v-if="!state.isVisible"
			style="
				height: 100vh;
				display: flex;
				justify-content: center;
				align-items: center;
			"
		>
			<el-card class="upload-success-card">
				<div class="upload-success-card__content">
					<div class="success-icon">
						<!-- Inline SVG for success check -->
						<svg
							width="48"
							height="48"
							viewBox="0 0 48 48"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								id="Vector"
								fill-rule="evenodd"
								clip-rule="evenodd"
								d="M24 48C37.2548 48 48 37.2548 48 24C48 10.7452 37.2548 0 24 0C10.7452 0 0 10.7452 0 24C0 37.2548 10.7452 48 24 48ZM12.8284 22.4853L10 25.3137L19.8995 35.2132L38.2843 16.8284L35.4558 14L19.8995 29.5564L12.8284 22.4853Z"
								fill="#48BB78"
							/>
						</svg>
					</div>
					<div class="upload-success-card__title">
						{{ t("UPLOADED_SUCCESSFULLY") }}
					</div>
					<p class="complete-notification-message">
						{{ completeNotificationMessage }}
					</p>
				</div>
			</el-card>
		</el-container>
	</div>
</template>

<script setup lang="ts">
import { onMounted, reactive, computed } from "vue";
import type { Action } from 'element-plus'
import { ElMessageBox, ElMessage } from "element-plus";
import { useRoute } from "vue-router";
import { useI18n } from "vue-i18n";

import Config from "../../../config";
import ExternalUploadFileService from "../../services/external-upload-file.service";
import { handleLogSentry } from "../../utils/sentry";
import { PropertyDocument } from "../../types/property";
import "./styles.scss";

const { t } = useI18n();

const state = reactive({
	uploadingCount: 0,
	isVisible: true,
	isInitialLoading: true,
	loading: false,
	completeNotificationMessage: "",
	form: {
		PropertyId: null,
		PropertyReference: "",
		DocumentTypes: [],
		CustomerName: "",
		SiteName: "",
		UserName: "",
		ContactName: "",
		ContactEmail: ""
	} as PropertyDocument,
	fileList: [],
	propertyId: null,
	token: ""
});

const documentTypeNames = computed(() => {
	return state.form?.DocumentTypes?.map(
		(documentType) => documentType.Name
	)?.join(",");
});

const completeNotificationMessage = computed(() => {
	if (state.form?.PropertyReference) {
		const temp2 = state.fileList
			.map((file: any) => `[${file?.response?.FileName}]`)
			.join(", ");
		const temp = t("UPLOADED_SUCCESSFULLY_CONTENT", {
			documents: temp2,
			documentTypes: `[${state.form.PropertyReference}]`
		});
		return temp;
	}
	return "";
});

const handleRemove = (file: any, fileList: any) => {
	if (file?.status === 'uploading') {
		state.uploadingCount--;
	}
	state.fileList = fileList;
};

const handleError = (err: any, file: any, fileList: any) => {
	// Handle upload error
	state.uploadingCount--; // Decrement uploading count
	ElMessage.error({message: t("UPLOAD_FILE_FAILED", {fileName: file.name}).toString(), duration: 5000});
};

const handleBeforeUpload = (file: any) => {
	// Increment uploading count when uploading starts
	state.uploadingCount++;
	return true; // Allow upload to continue
};

const handleSuccess = (response: any, file: any, fileList: any) => {
	state.uploadingCount--;
	state.fileList = fileList;
};

const submitForm = async () => {
	state.loading = true;
	const payload = {
		DocumentInfos: [] as any[]
	};

	if (state.fileList.length > 0) {
		const documentFilesInfo: any = {
			DocumentType: state.form.DocumentTypes[0].Id,
			Files: state.fileList.map((file: any) => {
				return {
					Url: file.response.Url,
					FileName: file.response.FileName,
					Comment: file.response.FileName
				};
			})
		};
		payload.DocumentInfos.push(documentFilesInfo as any);
	}

	const res = await ExternalUploadFileService.UploadPropertyDocuments(
		state.token,
		payload
	);

	state.loading = false;

	if (res.status === 200) {
		state.isVisible = false;
	}
};

onMounted(async () => {
	try {
		state.token = useRoute().query.token as string;
		if (!state.token) {
			ElMessage.error({message: t("TOKEN_REQUIRED").toString(), duration: 9999999});
			return;
		}

		
		state.form = await ExternalUploadFileService.GetPropertyDocumentInfo(
			state.token
		);

		if (!state.form) {
			ElMessage.error({message: t("AUTHENTICATION_FAILED").toString(), duration: 9999999});
			return;
		}
		
		handleLogSentry({...state.form, token: state.token});
		
		state.isInitialLoading = false;
		
	} catch (error) {
		console.error("Error loading document info:", error);
		state.isInitialLoading = false;
	}
});
</script>

<style scoped>
/* Skeleton Loading Styles */
.skeleton-loading {
	animation: none;
	display: block;
}

.skeleton-text, .skeleton-upload-box, .skeleton-button {
	background: linear-gradient(90deg, #ffffff 0%, #dde7fb 50%, #ffffff 100%) !important;
	background-size: 200% 100% !important;
	animation: shimmer 1s infinite !important;
	border-radius: 6px !important;
	height: 28px !important;
	display: block !important;
	overflow: hidden !important;
	position: relative !important;
	border-radius: 20px !important;
}

.skeleton-text--short {
	width: 120px !important;
	height: 24px !important;
	min-width: 120px !important;
}

.skeleton-text--medium {
	width: 180px !important;
	height: 24px !important;
	min-width: 180px !important;
}

.skeleton-text--long {
	width: 250px !important;
	height: 24px !important;
	min-width: 250px !important;
}

.skeleton-button {
	width: 150px !important;
	height: 45px !important;
	border-radius: 6px !important;
	margin-top: 15px !important;
	background: linear-gradient(90deg, #3b82f6 0%, #60a5fa 50%, #3b82f6 100%) !important;
}

/* Shimmer Animation */
@keyframes shimmer {
	0% {
		background-position: -200% 0;
	}
	100% {
		background-position: 200% 0;
	}
}

/* Fallback nếu gradient không work */
.skeleton-text:before, .skeleton-upload-box:before, .skeleton-button:before {
	content: '';
	display: block;
	width: 100%;
	height: 100%;
	background: #e0e0e0;
	border-radius: inherit;
	animation: pulse 1.5s ease-in-out infinite alternate;
}

@keyframes pulse {
	0% {
		opacity: 1;
	}
	100% {
		opacity: 0.4;
	}
}


.loading-card {
	min-width: 300px;
	text-align: center;
	border-radius: 8px;
}

.loading-card__content {
	padding: 40px 20px;
}

.loading-spinner {
	height: 80px;
	position: relative;
	margin-bottom: 20px;
}

.loading-text {
	font-size: 16px;
	color: #606266;
	font-weight: 500;
}

/* Animation cho loading spinner */
@keyframes spin {
	0% { transform: rotate(0deg); }
	100% { transform: rotate(360deg); }
}

.loading-spinner::before {
	content: '';
	position: absolute;
	top: 50%;
	left: 50%;
	width: 40px;
	height: 40px;
	margin: -20px 0 0 -20px;
	border: 3px solid #f3f3f3;
	border-top: 3px solid #409eff;
	border-radius: 50%;
	animation: spin 1s linear infinite;
}
</style>	